import React, { useEffect, useMemo, useState } from 'react';
import { addressOf, assignmentDescription, computeLineTotals } from '../../utils/billingUtils';

function isoDate(date = new Date()) {
  const offset = date.getTimezoneOffset();
  return new Date(date.getTime() - offset * 60000).toISOString().slice(0, 10);
}

export default function ProformaFormModal({ open, companies, clients, assignments, quickWorks = [], proforma = null, initialWork = null, onClose, onSave }) {
  const [form, setForm] = useState({
    companyId: '', clientId: '', proformaDate: isoDate(), dueDate: '', manualNumber: '',
    assignmentIds: [], quickWorkIds: [], lineItems: [], notes: '', paymentTerms: '', changeReason: '',
  });
  const [error, setError] = useState('');
  const selectedCompany = companies.find((item) => item.id === form.companyId);
  const selectedClient = clients.find((item) => item.id === form.clientId);

  useEffect(() => {
    if (!open) return;
    if (proforma) {
      const dateValue = (value) => {
        if (!value) return '';
        const date = value?.toDate ? value.toDate() : new Date(value);
        return Number.isNaN(date.getTime()) ? '' : isoDate(date);
      };
      setForm({
        companyId: proforma.companyId || '',
        clientId: proforma.clientId || '',
        proformaDate: dateValue(proforma.proformaDate) || isoDate(),
        dueDate: dateValue(proforma.dueDate),
        manualNumber: '',
        assignmentIds: proforma.assignmentIds || [],
        quickWorkIds: proforma.quickWorkIds || [],
        lineItems: (proforma.lineItems || []).map((line, index) => ({
          ...line,
          id: line.id || `line-${index + 1}`,
          amount: line.amount === 0 ? '0' : String(line.amount ?? ''),
        })),
        notes: proforma.notes || '',
        paymentTerms: proforma.paymentTerms || '',
        changeReason: '',
      });
    } else {
      const company = companies.find((item) => item.isActive !== false);
      const seededAssignment = initialWork?.type === 'assignment' ? assignments.find((item) => item.id === initialWork.id) : null;
      const seededQuickWork = initialWork?.type === 'quickwork' ? quickWorks.find((item) => item.id === initialWork.id) : null;
      const seed = seededAssignment || seededQuickWork;
      setForm({
        companyId: company?.id || '', clientId: seed?.clientId || '', proformaDate: isoDate(), dueDate: '', manualNumber: '',
        assignmentIds: seededAssignment ? [seededAssignment.id] : [],
        quickWorkIds: seededQuickWork ? [seededQuickWork.id] : [],
        lineItems: seededAssignment ? [{ id: `assignment-${seededAssignment.id}`, assignmentId: seededAssignment.id, quickWorkId: '', description: assignmentDescription(seededAssignment), amount: '' }]
          : seededQuickWork ? [{ id: `quickwork-${seededQuickWork.id}`, assignmentId: '', quickWorkId: seededQuickWork.id, description: [seededQuickWork.nature, seededQuickWork.remarks].filter(Boolean).join(' - '), amount: '' }]
          : [],
        notes: '', paymentTerms: company?.paymentTerms || '', changeReason: '',
      });
    }
    setError('');
  }, [open, companies, proforma, initialWork, assignments, quickWorks]);

  const eligibleAssignments = useMemo(() => assignments.filter((item) => {
    if (!form.clientId || item.clientId !== form.clientId) return false;
    if (item.isArchived) return false;
    return item.billingCompleted !== true && (!item.proformaId || item.proformaId === proforma?.id);
  }), [assignments, form.clientId, proforma]);

  const eligibleQuickWorks = useMemo(() => quickWorks.filter((item) => {
    if (!form.clientId || item.clientId !== form.clientId) return false;
    if (item.status !== 'Completed') return false;
    return item.billingCompleted !== true && (!item.proformaId || item.proformaId === proforma?.id);
  }), [quickWorks, form.clientId, proforma]);

  const totals = computeLineTotals(form.lineItems, selectedCompany?.gstApplicable, selectedCompany?.gstRate);

  if (!open) return null;
  const setValue = (key, value) => setForm((previous) => ({ ...previous, [key]: value }));

  const selectAssignment = (assignment, checked) => {
    setForm((previous) => {
      if (checked) {
        return {
          ...previous,
          assignmentIds: [...previous.assignmentIds, assignment.id],
          lineItems: [...previous.lineItems, {
            id: `assignment-${assignment.id}`,
            assignmentId: assignment.id,
            description: assignmentDescription(assignment),
            amount: '',
          }],
        };
      }
      return {
        ...previous,
        assignmentIds: previous.assignmentIds.filter((id) => id !== assignment.id),
        lineItems: previous.lineItems.filter((line) => line.assignmentId !== assignment.id),
      };
    });
  };

  const selectQuickWork = (quickWork, checked) => {
    setForm((previous) => {
      if (checked) {
        return {
          ...previous,
          quickWorkIds: [...previous.quickWorkIds, quickWork.id],
          lineItems: [...previous.lineItems, {
            id: `quickwork-${quickWork.id}`,
            quickWorkId: quickWork.id,
            assignmentId: '',
            description: [quickWork.nature, quickWork.remarks].filter(Boolean).join(' - '),
            amount: '',
          }],
        };
      }
      return {
        ...previous,
        quickWorkIds: previous.quickWorkIds.filter((id) => id !== quickWork.id),
        lineItems: previous.lineItems.filter((line) => line.quickWorkId !== quickWork.id),
      };
    });
  };

  const addManualLine = () => setForm((previous) => ({
    ...previous,
    lineItems: [...previous.lineItems, { id: `manual-${Date.now()}`, assignmentId: '', quickWorkId: '', description: '', amount: '' }],
  }));

  const updateLine = (index, key, value) => setForm((previous) => ({
    ...previous,
    lineItems: previous.lineItems.map((line, lineIndex) => lineIndex === index ? { ...line, [key]: value } : line),
  }));

  const removeLine = (index) => setForm((previous) => {
    const removed = previous.lineItems[index];
    return {
      ...previous,
      lineItems: previous.lineItems.filter((_, lineIndex) => lineIndex !== index),
      assignmentIds: removed.assignmentId ? previous.assignmentIds.filter((id) => id !== removed.assignmentId) : previous.assignmentIds,
      quickWorkIds: removed.quickWorkId ? previous.quickWorkIds.filter((id) => id !== removed.quickWorkId) : previous.quickWorkIds,
    };
  });

  const submit = (event) => {
    event.preventDefault();
    if (!form.companyId) return setError('Select a billing company.');
    if (!form.clientId) return setError('Select a client.');
    if (!form.lineItems.length) return setError('Add at least one billing line.');
    if (form.lineItems.some((line) => !line.description.trim() || Number(line.amount) <= 0)) return setError('Each billing line requires description and a positive amount.');
    setError('');
    onSave({
      ...form,
      taxableAmount: totals.taxableAmount,
      gstRate: selectedCompany?.gstApplicable ? Number(selectedCompany.gstRate || 0) : 0,
      gstAmount: totals.gstAmount,
      grandTotal: totals.grandTotal,
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-3">
      <form onSubmit={submit} className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[96vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between z-10">
          <div><h2 className="text-xl font-bold">{proforma ? 'Edit Proforma' : 'Create Proforma'}</h2><p className="text-xs text-gray-500">{proforma ? 'Edit the Draft, including billing company, descriptions, amounts and terms.' : 'Select completed work or add a manual billing line.'}</p></div>
          <button type="button" onClick={onClose} className="text-2xl">×</button>
        </div>
        <div className="p-6">
          {error && <div className="mb-4 p-3 bg-red-100 text-red-800 rounded">{error}</div>}
          <div className="grid md:grid-cols-3 gap-4">
            <label className="text-sm">Billing Company<select className="mt-1 w-full border rounded px-3 py-2" value={form.companyId} onChange={(e) => { const company = companies.find((item) => item.id === e.target.value); setForm((p) => ({ ...p, companyId: e.target.value, paymentTerms: company?.paymentTerms || '' })); }}><option value="">Select</option>{companies.filter((item) => item.isActive !== false).map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>
            <label className="text-sm">Client<select disabled={Boolean(proforma)} className="mt-1 w-full border rounded px-3 py-2" value={form.clientId} onChange={(e) => setForm((p) => ({ ...p, clientId: e.target.value, assignmentIds: [], quickWorkIds: [], lineItems: [] }))}><option value="">Select</option>{clients.filter((item) => item.status !== 'Inactive').sort((a,b) => a.name.localeCompare(b.name)).map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>
            {!proforma && <label className="text-sm">Manual Number Override<input className="mt-1 w-full border rounded px-3 py-2" placeholder="Leave blank for automatic" value={form.manualNumber} onChange={(e) => setValue('manualNumber', e.target.value)} /></label>}
            <label className="text-sm">Proforma Date<input type="date" className="mt-1 w-full border rounded px-3 py-2" value={form.proformaDate} onChange={(e) => setValue('proformaDate', e.target.value)} /></label>
            <label className="text-sm">Due Date<input type="date" className="mt-1 w-full border rounded px-3 py-2" value={form.dueDate} onChange={(e) => setValue('dueDate', e.target.value)} /></label>
            <div className="text-sm bg-gray-50 border rounded p-3"><div className="text-gray-500">Client Address</div><div>{selectedClient ? addressOf(selectedClient) || 'Not available' : 'Select client'}</div></div>
          </div>

          <div className="grid lg:grid-cols-3 gap-5 mt-6">
            <div className="lg:col-span-1 space-y-4">
              <div className="border rounded">
                <div className="bg-gray-100 px-4 py-3 font-semibold">Eligible Assignments</div>
                <div className="max-h-56 overflow-y-auto">
                  {!form.clientId && <div className="p-4 text-sm text-gray-500">Select a client first.</div>}
                  {form.clientId && !eligibleAssignments.length && <div className="p-4 text-sm text-gray-500">No unbilled assignments for this client.</div>}
                  {eligibleAssignments.map((assignment) => <label key={assignment.id} className="flex gap-3 p-3 border-t hover:bg-gray-50 cursor-pointer"><input type="checkbox" checked={form.assignmentIds.includes(assignment.id)} onChange={(e) => selectAssignment(assignment, e.target.checked)} /><span><span className="font-medium block">{assignment.assignmentType || assignment.category}</span><span className="text-xs text-gray-500">{[assignment.financialYear, assignment.period, assignment.status].filter(Boolean).join(' · ')}</span></span></label>)}
                </div>
              </div>
              <div className="border rounded">
                <div className="bg-amber-50 px-4 py-3 font-semibold">Completed Quick Works</div>
                <div className="max-h-56 overflow-y-auto">
                  {!form.clientId && <div className="p-4 text-sm text-gray-500">Select a client first.</div>}
                  {form.clientId && !eligibleQuickWorks.length && <div className="p-4 text-sm text-gray-500">No completed unbilled Quick Works for this client.</div>}
                  {eligibleQuickWorks.map((item) => <label key={item.id} className="flex gap-3 p-3 border-t hover:bg-gray-50 cursor-pointer"><input type="checkbox" checked={form.quickWorkIds.includes(item.id)} onChange={(e) => selectQuickWork(item, e.target.checked)} /><span><span className="font-medium block">{item.nature}</span><span className="text-xs text-gray-500">{item.remarks || 'No remark'}</span></span></label>)}
                </div>
              </div>
            </div>
            <div className="lg:col-span-2 border rounded overflow-hidden">
              <div className="bg-gray-100 px-4 py-3 flex justify-between items-center"><span className="font-semibold">Billing Lines</span><button type="button" onClick={addManualLine} className="text-blue-700 text-sm">+ Manual Line</button></div>
              <div className="overflow-x-auto"><table className="w-full text-sm"><thead><tr><th className="text-left p-3">Description</th><th className="text-right p-3 w-40">Amount</th><th className="w-12"></th></tr></thead><tbody>{form.lineItems.map((line, index) => <tr key={line.id} className="border-t"><td className="p-2"><input className="w-full border rounded px-3 py-2" value={line.description} onChange={(e) => updateLine(index, 'description', e.target.value)} /></td><td className="p-2"><input type="number" min="0" step="0.01" className="w-full border rounded px-3 py-2 text-right" value={line.amount} onChange={(e) => updateLine(index, 'amount', e.target.value)} /></td><td className="p-2"><button type="button" onClick={() => removeLine(index)} className="text-red-600">×</button></td></tr>)}</tbody></table></div>
              {!form.lineItems.length && <div className="p-8 text-center text-gray-500">Select assignments, completed Quick Works or add a manual line.</div>}
              <div className="border-t p-4 ml-auto max-w-sm text-sm space-y-2">
                <div className="flex justify-between"><span>Taxable Amount</span><strong>₹ {totals.taxableAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</strong></div>
                {selectedCompany?.gstApplicable && <div className="flex justify-between"><span>GST @ {selectedCompany.gstRate || 0}%</span><strong>₹ {totals.gstAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</strong></div>}
                <div className="flex justify-between text-lg border-t pt-2"><span>Total</span><strong>₹ {totals.grandTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</strong></div>
              </div>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4 mt-5">
            <label className="text-sm">Payment Terms<textarea rows="3" className="mt-1 w-full border rounded px-3 py-2" value={form.paymentTerms} onChange={(e) => setValue('paymentTerms', e.target.value)} /></label>
            <label className="text-sm">Notes<textarea rows="3" className="mt-1 w-full border rounded px-3 py-2" value={form.notes} onChange={(e) => setValue('notes', e.target.value)} /></label>
            {proforma && Math.abs(Number(proforma.grandTotal || 0) - Number(totals.grandTotal || 0)) > 0.009 && <label className="text-sm md:col-span-2">Reason for Amount Change<textarea rows="2" className="mt-1 w-full border rounded px-3 py-2" value={form.changeReason} onChange={(e) => setValue('changeReason', e.target.value)} placeholder="Optional audit note" /></label>}
          </div>
          <div className="flex justify-end gap-2 mt-6"><button type="button" onClick={onClose} className="px-4 py-2 border rounded">Cancel</button><button type="submit" className="px-5 py-2 bg-blue-600 text-white rounded">{proforma ? 'Save Changes' : 'Create Proforma'}</button></div>
        </div>
      </form>
    </div>
  );
}
